import React, { FunctionComponent, useState } from 'react';
import { ContactCourierCard as UIContactCourierCard, ContactCourierCardProps } from '@shopee/chatbot-ui';
import { ContactCourierCard as ContactCourierCardV2 } from '@shopee/chatbot-ui/rn/components/ContactCourierCardV2/rn';
import { ButtonEvents } from '@shopee/chatbot-ui/rn/types';

import { bridgeCallFunc } from '@/utils/instantLoaded/tools';
import { ChatbotUIBasicProps } from '@/types/common';
import { TRACKING_SECTION, TRACKING_TARGET, TRACKING_TYPE } from '@/utils/tracking/constants';
import { TRACKING_MSG_TYPE, CONTACT_COURIER_CARD } from '@/utils/constants';
import { getTrans } from '@/utils/tools';
import { eventEmitter } from '@/utils/chatuiEvents';
import { getButtonAction } from '@/utils/getButtonData';

import Webform, { WebFormProps } from '../Webform';
import { getRedirectCallbackParams } from './Message';

enum ContactCourierCardVersion {
  Old = 0,
  V2 = 1,
}

export type Props = ContactCourierCardProps & ChatbotUIBasicProps & { version?: ContactCourierCardVersion };
export enum ContactCourierCardTrackingType {
  ContactCourierCardButton = 'ContactCourierCardButton',
  ContactCourierCardCallDriver = 'ContactCourierCardCallDriver',
  ContactCourierCardCopyPaste = 'ContactCourierCardCopyPaste',
}

const ContactCourierCard: FunctionComponent<Props> = (props) => {
  const { orderInfo, version, customCallback } = props;
  const [webformProps, setWebformProps] = useState<WebFormProps | null>(null);
  const handleCopyClick = (text?: string | number) => {
    const copyText = text ?? orderInfo?.shipping_trace_no ?? '';
    if (copyText) {
      const params = { text: copyText };
      bridgeCallFunc(() => {
        window.bridgeCallHandler('copyTextToClipboard', params);
      });
    }
  };

  const trackItem = {
    chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
    chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
    node_point_id: props.nodePointId,
    if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
    kb_bot_id: props.kbBotId,
    tf_node_id: props.tfNodeId,
    tf_case_id: props.tfCaseId,
    taskbot_id: props.taskbotInfo?.task_bot_id,
    taskflow_id: props.taskbotInfo?.flow_id,
    button_text: '',
    button_type: '',
  };

  const componentTrackingData = {
    ContactCourierCardButton_call: {
      trackingsection: TRACKING_SECTION[TRACKING_TYPE.CONTACT_COURIER_CARD],
      trackingtarget: TRACKING_TARGET[TRACKING_TYPE.CONTACT_COURIER_CARD_BUTTON],
      trackingdataBasic: { data: { ...trackItem, button_text: getTrans(CONTACT_COURIER_CARD.CALL), button_type: 'call' }},
    },
    ContactCourierCardButton_facebook: {
      trackingsection: TRACKING_SECTION[TRACKING_TYPE.CONTACT_COURIER_CARD],
      trackingtarget: TRACKING_TARGET[TRACKING_TYPE.CONTACT_COURIER_CARD_BUTTON],
      trackingdataBasic: { data: { ...trackItem, button_text: getTrans(CONTACT_COURIER_CARD.FACEBOOK), button_type: 'facebook' }},
    },
    ContactCourierCardButton_whatsapp: {
      trackingsection: TRACKING_SECTION[TRACKING_TYPE.CONTACT_COURIER_CARD],
      trackingtarget: TRACKING_TARGET[TRACKING_TYPE.CONTACT_COURIER_CARD_BUTTON],
      trackingdataBasic: { data: { ...trackItem, button_text: getTrans(CONTACT_COURIER_CARD.WHATS_APP), button_type: 'whatsapp' }},
    },
    ContactCourierCardButton_email: {
      trackingsection: TRACKING_SECTION[TRACKING_TYPE.CONTACT_COURIER_CARD],
      trackingtarget: TRACKING_TARGET[TRACKING_TYPE.CONTACT_COURIER_CARD_BUTTON],
      trackingdataBasic: { data: { ...trackItem, button_text: getTrans(CONTACT_COURIER_CARD.EMAIL), button_type: 'email' }},
    },
    ContactCourierCardButton_trackOrder: {
      trackingsection: TRACKING_SECTION[TRACKING_TYPE.CONTACT_COURIER_CARD],
      trackingtarget: TRACKING_TARGET[TRACKING_TYPE.CONTACT_COURIER_CARD_BUTTON],
      trackingdataBasic: { data: { ...trackItem, button_text: getTrans(CONTACT_COURIER_CARD.EMAIL), button_type: 'trackOrder' }},
    },
    ContactCourierCardCallDriver_driverCall: {
      trackingsection: TRACKING_SECTION[TRACKING_TYPE.CONTACT_COURIER_CARD],
      trackingtarget: TRACKING_TARGET[TRACKING_TYPE.CONTACT_COURIER_CARD_CALL_DRIVER_BUTTON],
      trackingdataBasic: { data: { ...trackItem, button_text: 'driverCall', button_type: 'driverCall' }},
    },
    ContactCourierCardCopyPaste_copy: {
      trackingsection: TRACKING_SECTION[TRACKING_TYPE.CONTACT_COURIER_CARD],
      trackingtarget: TRACKING_TARGET[TRACKING_TYPE.CONTACT_COURIER_CARD_COPY_PASTE_BUTTON],
      trackingdataBasic: { data: { ...trackItem, button_text: 'copy', button_type: 'copy' }},
    },
  };

  if (version === ContactCourierCardVersion.V2) {
    return (
      <div>
        <ContactCourierCardV2
          {...props}
          handleCopyClick={handleCopyClick}
          isHistory={props.isHistory}
          onWebformButtonClick={(webformButton) => {
            const buttonData = {
              ...webformButton,
              action: {
                ...webformButton.action,
                func: getButtonAction(webformButton.action?.action_type),
                info: props,
                buttonInfo: { ...webformButton },
                taskbotInfo: props.taskbotInfo,
                customCallback,
              },
              multiple_action: {
                ...webformButton.multiple_action,
                func: getButtonAction(webformButton?.multiple_action?.action_type),
                info: props,
                buttonInfo: { ...webformButton },
                taskbotInfo: props.taskbotInfo,
                customCallback,
              },
            };
            if (buttonData.action.buttonInfo.callback_action) {
              buttonData.action.buttonInfo.callback_action = getRedirectCallbackParams({
                action: buttonData.action,
                btn: webformButton,
                props,
              });
            }
            if (buttonData.multiple_action.buttonInfo.callback_action) {
              buttonData.multiple_action.buttonInfo.callback_action = getRedirectCallbackParams({
                action: buttonData.multiple_action,
                btn: webformButton,
                props,
              });
            }

            eventEmitter.emit(ButtonEvents.PopupWebform, buttonData, (props: WebFormProps) => {
              setWebformProps(props);
            });
          }}
          componentTrackingData={{
            button: {
              trackingsection: TRACKING_SECTION[TRACKING_TYPE.NEW_CONTACT_COURIER_CARD_BUTTON],
              trackingtarget: TRACKING_TARGET[TRACKING_TYPE.NEW_CONTACT_COURIER_CARD_BUTTON],
              trackingdataBasic: trackItem,
            },
            contactButton: {
              trackingsection: TRACKING_SECTION[TRACKING_TYPE.NEW_CONTACT_COURIER_CARD_CONTACT_BUTTON],
              trackingtarget: TRACKING_TARGET[TRACKING_TYPE.NEW_CONTACT_COURIER_CARD_CONTACT_BUTTON],
              trackingdataBasic: trackItem,
            },
            popupButton: {
              trackingsection: TRACKING_SECTION[TRACKING_TYPE.NEW_CONTACT_COURIER_CARD_POPUP_BUTTON],
              trackingtarget: TRACKING_TARGET[TRACKING_TYPE.NEW_CONTACT_COURIER_CARD_POPUP_BUTTON],
              trackingdataBasic: trackItem,
            },
          }}
        />
        <Webform
          {...(webformProps || {})}
          isHistory={props.isHistory}
          historySessionId={props.historySessionId}
          sessionId={props.sessionId}
          historyDialogueId={props.historyDialogueId}
          dialogueId={props.dialogueId}
          listPos={props.listPos}
          onClosePopup={() => setWebformProps({ ...webformProps, active: false } as WebFormProps)}
        />
      </div>
    );
  }

  return (
    <UIContactCourierCard {...props} handleCopyClick={handleCopyClick} componentTrackingData={componentTrackingData} />
  );
};

export default ContactCourierCard;
